import { create } from 'zustand';
import {
  BridgeWarning,
  LiveTranscriptTurn,
  WebchatConversation,
  WebchatMessage,
} from '@/services/webchat-service/webchat.service.types';

export interface BridgeLogEntry {
  id: string;
  role: 'visitor' | 'agent';
  status: string;
  startedAt: number; // Date.now() ms
  durationMs: number | null; // null while step is still in progress
}

interface WebchatStore {
  sessions: WebchatConversation[];
  selectedConversationId: number | null;
  messages: Record<number, WebchatMessage[]>;
  visitorTyping: Record<number, boolean>;
  disconnectedVisitors: Set<number>;
  activeTab: 'live' | 'history';
  soundEnabled: boolean;
  translateEnabled: boolean;
  targetLanguage: string;
  // When true, live transcript bubbles render the original-language text
  // (Polish, Spanish, etc) straight from Scribe. When false (default), they
  // render the English translation produced by gpt-5.4-nano. Per-rep view
  // setting, not persisted server-side.
  showOriginalTranscript: boolean;
  bridgeStatus: Record<number, { visitor: string; agent: string }>;
  // Agent language for the active bridge call — populated from session_assigned
  // so BridgeStatusBar can show "[caller lang] ↔ [agent lang]" correctly.
  bridgeAgentLanguage: Record<number, string>;
  // Non-fatal warnings the LLM bridge surfaces during a call (currently:
  // "voice was rejected by ElevenLabs, fell back to default"). Keyed by
  // conversation; the most-recent warning per conversation is kept so the
  // sidebar can render it. Cleared when the conversation closes.
  bridgeWarnings: Record<number, BridgeWarning>;
  // Per-conversation ordered log of translation pipeline steps.
  // Each entry is appended on every non-idle bridge_status event;
  // durationMs is filled when the next step begins.
  bridgeLogs: Record<number, BridgeLogEntry[]>;

  setSessions: (sessions: WebchatConversation[]) => void;
  upsertSession: (session: WebchatConversation) => void;
  removeSession: (conversationId: number) => void;
  setSelectedConversationId: (id: number | null) => void;
  setMessages: (conversationId: number, messages: WebchatMessage[]) => void;
  addMessage: (conversationId: number, message: WebchatMessage) => void;
  appendLiveTranscriptTurn: (conversationId: number, turn: LiveTranscriptTurn) => void;
  setVisitorTyping: (conversationId: number, typing: boolean) => void;
  setDisconnected: (conversationId: number, disconnected: boolean) => void;
  setBridgeStatus: (conversationId: number, role: string, status: string) => void;
  setBridgeAgentLanguage: (conversationId: number, language: string) => void;
  setBridgeWarning: (conversationId: number, warning: BridgeWarning) => void;
  clearBridgeWarning: (conversationId: number) => void;
  addBridgeLogEntry: (conversationId: number, entry: BridgeLogEntry) => void;
  updateBridgeLogDuration: (conversationId: number, entryId: string, durationMs: number) => void;
  clearBridgeLogs: (conversationId: number) => void;
  setActiveTab: (tab: 'live' | 'history') => void;
  toggleSound: () => void;
  toggleTranslate: () => void;
  setTranslateEnabled: (enabled: boolean) => void;
  setTargetLanguage: (lang: string) => void;
  toggleShowOriginalTranscript: () => void;
}

export const useWebchatStore = create<WebchatStore>((set) => ({
  sessions: [],
  selectedConversationId: null,
  messages: {},
  visitorTyping: {},
  disconnectedVisitors: new Set(),
  activeTab: 'live',
  soundEnabled: true,
  translateEnabled: false,
  targetLanguage: 'en-us',
  showOriginalTranscript: false,
  bridgeStatus: {},
  bridgeAgentLanguage: {},

  setSessions: (sessions) => set({ sessions }),

  upsertSession: (session) =>
    set((state) => {
      const index = state.sessions.findIndex((s) => s.id === session.id);
      if (index >= 0) {
        const updated = [...state.sessions];
        updated[index] = session;
        return { sessions: updated };
      }
      return { sessions: [session, ...state.sessions] };
    }),

  removeSession: (conversationId) =>
    set((state) => ({
      sessions: state.sessions.filter((s) => s.id !== conversationId),
      selectedConversationId:
        state.selectedConversationId === conversationId ? null : state.selectedConversationId,
    })),

  setSelectedConversationId: (id) => set({ selectedConversationId: id }),

  setMessages: (conversationId, messages) =>
    set((state) => ({
      messages: { ...state.messages, [conversationId]: messages },
    })),

  addMessage: (conversationId, message) =>
    set((state) => {
      const existing = state.messages[conversationId] || [];
      // Dedup by message ID
      if (existing.some((m) => m.id === message.id)) return state;
      return {
        messages: { ...state.messages, [conversationId]: [...existing, message] },
      };
    }),

  // Append a live transcript turn to the matching session, deduping by
  // received_at + role + text. No-op if the session is not in the store.
  appendLiveTranscriptTurn: (conversationId, turn) =>
    set((state) => {
      const index = state.sessions.findIndex((s) => s.id === conversationId);
      if (index < 0) return state;

      const session = state.sessions[index];
      const existing = session.live_transcript_turns ?? [];
      const isDuplicate = existing.some(
        (t) =>
          t.received_at === turn.received_at &&
          t.role === turn.role &&
          t.text === turn.text,
      );
      if (isDuplicate) return state;

      const updatedSession: WebchatConversation = {
        ...session,
        live_transcript_turns: [...existing, turn],
      };
      const updatedSessions = [...state.sessions];
      updatedSessions[index] = updatedSession;
      return { sessions: updatedSessions };
    }),

  setVisitorTyping: (conversationId, typing) =>
    set((state) => ({
      visitorTyping: { ...state.visitorTyping, [conversationId]: typing },
    })),

  setDisconnected: (conversationId, disconnected) =>
    set((state) => {
      const next = new Set(state.disconnectedVisitors);
      if (disconnected) {
        next.add(conversationId);
      } else {
        next.delete(conversationId);
      }
      return { disconnectedVisitors: next };
    }),

  setBridgeStatus: (conversationId, role, status) =>
    set((state) => ({
      bridgeStatus: {
        ...state.bridgeStatus,
        [conversationId]: {
          ...(state.bridgeStatus[conversationId] || { visitor: 'idle', agent: 'idle' }),
          [role]: status,
        },
      },
    })),

  setBridgeAgentLanguage: (conversationId, language) =>
    set((state) => ({
      bridgeAgentLanguage: { ...state.bridgeAgentLanguage, [conversationId]: language },
    })),

  bridgeWarnings: {},
  bridgeLogs: {},

  setBridgeWarning: (conversationId, warning) =>
    set((state) => ({
      bridgeWarnings: { ...state.bridgeWarnings, [conversationId]: warning },
    })),

  clearBridgeWarning: (conversationId) =>
    set((state) => {
      const next = { ...state.bridgeWarnings };
      delete next[conversationId];
      return { bridgeWarnings: next };
    }),

  addBridgeLogEntry: (conversationId, entry) =>
    set((state) => ({
      bridgeLogs: {
        ...state.bridgeLogs,
        [conversationId]: [...(state.bridgeLogs[conversationId] ?? []), entry],
      },
    })),

  updateBridgeLogDuration: (conversationId, entryId, durationMs) =>
    set((state) => {
      const entries = state.bridgeLogs[conversationId];
      if (!entries) return state;
      return {
        bridgeLogs: {
          ...state.bridgeLogs,
          [conversationId]: entries.map((e) =>
            e.id === entryId ? { ...e, durationMs } : e
          ),
        },
      };
    }),

  clearBridgeLogs: (conversationId) =>
    set((state) => {
      const next = { ...state.bridgeLogs };
      delete next[conversationId];
      return { bridgeLogs: next };
    }),

  setActiveTab: (tab) => set({ activeTab: tab }),

  toggleSound: () => set((state) => ({ soundEnabled: !state.soundEnabled })),

  toggleTranslate: () => set((state) => ({ translateEnabled: !state.translateEnabled })),

  setTranslateEnabled: (enabled) => set({ translateEnabled: enabled }),

  setTargetLanguage: (lang) => set({ targetLanguage: lang }),

  toggleShowOriginalTranscript: () =>
    set((state) => ({ showOriginalTranscript: !state.showOriginalTranscript })),
}));